import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClock,
  faCertificate,
  faIndianRupeeSign,
  faCheck,
  faQuoteLeft,
} from "@fortawesome/free-solid-svg-icons";

import { colleges, findCollege } from "../data/colleges";
import Figure from "./Media";
import Marquee from "./Marquee";
import { useReveal } from "../lib/motion";
import { COURSE_SHOT, SHOTS } from "../data/imagery";
import COURSE_ART from "./CourseArt";
import "./College.css";

/* ---------------------------------------------------------------------------
   Four colleges, four architectures, one palette.

   The pages deliberately do NOT share a layout. Each one is composed around
   the shape of its own courses:

     Medical      a ladder    — ANM → GNM → B.Sc is a real progression, so the
                               page climbs, and the rungs are ordered
     Allied       a panel     — four peer disciplines with no hierarchy, so
                               they get four equal quadrants
     Management   a bento     — four unlike things (a business degree, a
                               computing degree, two crafts), so tiles differ
                               in size and weight
     Law          a case file — two routes to one bar, so two facing sheets

   What they DO share: the palette, the buttons, the type scale, the picture
   component, the closing furniture. Colour never identifies the college here
   — layout does. `--tint` is allowed on chips and small marks only.
--------------------------------------------------------------------------- */

const Reveal = ({ children, className = "", as: Tag = "div" }) => {
  const [ref, shown] = useReveal();
  return (
    <Tag ref={ref} className={`reveal ${shown ? "is-visible" : ""} ${className}`}>
      {children}
    </Tag>
  );
};

/* --------------------------------------------------------- shared pieces */

const Specs = ({ course }) => (
  <ul className="specs figures">
    <li>
      <FontAwesomeIcon icon={faClock} />
      {course.duration}
    </li>
    {course.awarding && (
      <li>
        <FontAwesomeIcon icon={faCertificate} />
        {course.awarding}
      </li>
    )}
    {course.fee && (
      <li>
        <FontAwesomeIcon icon={faIndianRupeeSign} />
        {course.fee}
      </li>
    )}
  </ul>
);

const Entry = ({ course }) => (
  <details className="entry">
    <summary>Entry requirements</summary>
    <ul className="tick-list">
      {course.eligibility.map((e, i) => (
        <li key={i}>
          <FontAwesomeIcon icon={faCheck} />
          <span>{e}</span>
        </li>
      ))}
    </ul>
  </details>
);

const Chips = ({ items, max = 6 }) =>
  items ? (
    <ul className="chip-list">
      {items.slice(0, max).map((h) => (
        <li key={h}>{h}</li>
      ))}
    </ul>
  ) : null;

const Crumbs = ({ name, light = false }) => (
  <nav className={`crumbs ${light ? "crumbs--light" : ""}`} aria-label="Breadcrumb">
    <a href="/home">Group of Institutes</a>
    <span aria-hidden="true">/</span>
    <span>{name}</span>
  </nav>
);

/* the four colleges, this one lit — the one mark every page shares */
const Dots = ({ current, light = false }) => (
  <span className={`dots ${light ? "dots--light" : ""}`} aria-hidden="true">
    {colleges.map((c) => (
      <span
        key={c.slug}
        className={`dot ${c.slug === current ? "is-on" : ""}`}
        style={{ "--dot": `var(--v-${c.slug})` }}
      />
    ))}
  </span>
);

const CareersStrip = ({ college }) =>
  college.careers ? (
    <section className="careers-band">
      <div className="container">
        <Reveal className="section-head section-head--center">
          <p className="eyebrow">After you graduate</p>
          <h2 className="h-md">Where this takes you</h2>
        </Reveal>
      </div>
      <Marquee className="wordstrip" speed="mid">
        {college.careers.map((c) => (
          <span className="wordstrip-item" key={c} style={{ "--dot": `var(--v-${college.slug})` }}>
            {c}
          </span>
        ))}
      </Marquee>
    </section>
  ) : null;

const Siblings = ({ current }) => (
  <section className="section siblings">
    <div className="container">
      <Reveal className="section-head section-head--center">
        <p className="eyebrow">The group</p>
        <h2 className="h-md">Our other colleges</h2>
      </Reveal>
      <div className="sibling-grid">
        {colleges
          .filter((c) => c.slug !== current)
          .map((c) => (
            <a
              className="sibling"
              href={`/colleges/${c.slug}`}
              key={c.slug}
              style={{ "--tint": `var(--v-${c.slug})` }}
            >
              <Figure ratio="3 / 2" src={SHOTS.collegeCard[c.slug]} alt={c.name} label={c.name} size="900×600" />
              <span className="sibling-body">
                <span className="sibling-name seal-type">{c.name}</span>
                <span className="sibling-count figures">{c.courses.length} courses</span>
              </span>
            </a>
          ))}
      </div>
    </div>
  </section>
);

const CtaBand = ({ college }) => (
  <section className="cta-band">
    <div className="container cta-band-inner">
      <div>
        <p className="cta-band-eyebrow">Admissions open for 2026–27</p>
        <h2 className="cta-band-title seal-type">{college.name}</h2>
      </div>
      <a href="/admissions" className="btn btn--primary">
        Apply now
        <FontAwesomeIcon icon={faArrowRight} />
      </a>
    </div>
    <div className="ribbon" aria-hidden="true" />
  </section>
);

/* ============================================================ 1 · MEDICAL =
   A ladder. The three courses are a genuine progression — two years, three,
   four — so the page climbs and the rungs are numbered by duration, not by
   an arbitrary 01/02/03.
   ========================================================================= */

const Medical = ({ college }) => (
  <>
    <section className="ladder-hero">
      <Figure
        className="ladder-hero-bg"
        ratio="21 / 9"
        src={SHOTS.medicalHero}
        alt="Students on the ward during a clinical posting"
      />
      <div className="ladder-hero-veil" aria-hidden="true" />
      <div className="container ladder-hero-copy">
        <Crumbs name={college.name} light />
        <h1 className="page-title seal-type">{college.name}</h1>
        <p className="page-lead">{college.lead}</p>
        <div className="page-actions">
          <a href="#courses" className="btn btn--primary">
            Three ways in
            <FontAwesomeIcon icon={faArrowRight} />
          </a>
          <Dots current={college.slug} light />
        </div>
      </div>
    </section>

    <section className="section ladder" id="courses">
      <div className="container">
        <Reveal className="section-head">
          <p className="eyebrow">The nursing ladder</p>
          <h2 className="h-lg">Start anywhere. Keep climbing.</h2>
        </Reveal>

        <ol className="rungs">
          {[...college.courses].reverse().map((course) => (
            <Reveal className="rung" as="li" key={course.id}>
              <span className="rung-years figures" aria-hidden="true">
                {course.years}
                <em>yr</em>
              </span>
              <Figure
                className="rung-media"
                ratio="4 / 3"
                src={COURSE_SHOT[course.id]}
                alt={course.name}
                label={course.name}
                size="900×675"
              />
              <div className="rung-body">
                <span className="rung-code" style={{ "--tint": `var(--v-${college.slug})` }}>
                  {course.code}
                </span>
                <h3 className="rung-name">{course.name}</h3>
                <p className="rung-summary">{course.summary}</p>
                <Specs course={course} />
                <Entry course={course} />
                <a href="/admissions" className="btn btn--primary">
                  Apply
                  <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>

    <Marquee className="photostrip band-strip" speed="slow">
      {SHOTS.medicalStrip.map((shot) => (
        <Figure key={shot.alt} ratio="3 / 2" src={shot.src} alt={shot.alt} />
      ))}
    </Marquee>
  </>
);

/* ==================================================== 2 · ALLIED HEALTHCARE
   A panel of four. These disciplines are peers — nobody does optometry
   "after" radiology — so they get four equal quadrants rather than a list.
   ========================================================================= */

const Allied = ({ college }) => {
  const [open, setOpen] = useState(college.courses[0].id);
  const active = college.courses.find((c) => c.id === open);

  return (
    <>
      <section className="panel-hero">
        <div className="container panel-hero-grid">
          <Reveal>
            <Crumbs name={college.name} />
            <h1 className="page-title seal-type">{college.name}</h1>
            <p className="page-lead">{college.lead}</p>
            <div className="page-actions">
              <a href="#courses" className="btn btn--primary">
                Four disciplines
                <FontAwesomeIcon icon={faArrowRight} />
              </a>
              <Dots current={college.slug} />
            </div>
          </Reveal>
          <Reveal className="panel-hero-grid4">
            {college.courses.map((c) => (
              <Figure
                key={c.id}
                ratio="1 / 1"
                src={COURSE_SHOT[c.id]}
                art={COURSE_ART[c.id]}
                alt={c.discipline}
                label={c.discipline}
                size="700×700"
              />
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section panel" id="courses">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Courses offered</p>
            <h2 className="h-lg">Four rooms in the hospital</h2>
          </Reveal>

          <div className="quadrant">
            {college.courses.map((c) => (
              <button
                key={c.id}
                className={`quad ${c.id === open ? "is-on" : ""}`}
                style={{ "--tint": `var(--v-${college.slug})` }}
                onClick={() => setOpen(c.id)}
                aria-expanded={c.id === open}
              >
                <Figure
                  ratio="4 / 3"
                  src={COURSE_SHOT[c.id]}
                  art={COURSE_ART[c.id]}
                  alt={c.discipline}
                  label={c.discipline}
                  size="900×675"
                />
                <span className="quad-bar">
                  <span className="quad-icon">
                    <FontAwesomeIcon icon={c.icon} />
                  </span>
                  <span className="quad-name">{c.discipline}</span>
                  <span className="quad-dur figures">{c.duration}</span>
                </span>
              </button>
            ))}
          </div>

          <Reveal className="panel-detail" key={active.id}>
            <div className="panel-detail-main">
              <h3 className="panel-detail-name">{active.name}</h3>
              <p className="panel-detail-summary">{active.summary}</p>
              <Chips items={active.highlights} />
            </div>
            <div className="panel-detail-side">
              <Specs course={active} />
              <Entry course={active} />
              <a href="/admissions" className="btn btn--primary">
                Apply for {active.discipline}
                <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

/* ========================================================= 3 · MANAGEMENT =
   A bento. Four unlike things — a business degree, a computing degree and
   two crafts — so the tiles are deliberately unequal in size and shape.
   ========================================================================= */

const Management = ({ college }) => (
  <>
    <section className="bento-hero">
      <div className="container bento-hero-grid">
        <Reveal className="bento-hero-copy">
          <Crumbs name={college.name} />
          <h1 className="page-title seal-type">{college.name}</h1>
          <p className="page-lead">{college.lead}</p>
          <div className="page-actions">
            <a href="#courses" className="btn btn--primary">
              See the four
              <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <Dots current={college.slug} />
          </div>
        </Reveal>
        <Reveal className="bento-hero-stack">
          {SHOTS.managementHero.map((shot) => (
            <Figure
              key={shot.label}
              ratio={shot.ratio}
              src={shot.src}
              alt={shot.alt}
              label={shot.label}
              size={shot.size}
            />
          ))}
        </Reveal>
      </div>
    </section>

    <section className="section bento" id="courses">
      <div className="container">
        <Reveal className="section-head">
          <p className="eyebrow">Courses offered</p>
          <h2 className="h-lg">A degree, or a trade you can practise</h2>
        </Reveal>

        <div className="bento-grid">
          {college.courses.map((course, i) => (
            <Reveal
              className={`tile tile--${i + 1}`}
              as="article"
              key={course.id}
              style={{ "--tint": `var(--v-${college.slug})` }}
            >
              <Figure
                className="tile-media"
                ratio={i === 0 ? "16 / 9" : i === 1 ? "4 / 5" : "16 / 9"}
                src={COURSE_SHOT[course.id]}
                alt={course.discipline}
                label={course.discipline}
                size="1000×700"
              />
              <div className="tile-body">
                <span className="tile-code">{course.code}</span>
                <h3 className="tile-name">{course.discipline}</h3>
                <p className="tile-summary">{course.summary}</p>
                <Chips items={course.highlights} max={i === 0 ? 6 : 3} />
                <Specs course={course} />
                <Entry course={course} />
                <a href="/admissions" className="btn btn--primary">
                  Apply
                  <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

/* ================================================================ 4 · LAW =
   A case file. Two routes to the same bar, so two facing sheets — and the
   practical training, which is what the brochure actually sells, gets the
   room rather than the prose.
   ========================================================================= */

const Law = ({ college }) => (
  <>
    <section className="file-hero">
      <div className="container file-hero-grid">
        <Reveal className="file-hero-copy">
          <Crumbs name={college.name} light />
          <h1 className="page-title seal-type">{college.name}</h1>
          <p className="file-quote">
            <FontAwesomeIcon icon={faQuoteLeft} />
            Empowering future legal professionals with knowledge, ethics and
            excellence.
          </p>
          {/* the two routes, up front — this is the first thing anyone asks,
              and it fills the column the portrait would otherwise tower over */}
          <ul className="file-routes">
            {college.courses.map((c) => (
              <li key={c.id}>
                <a href="#courses" className="file-route">
                  <span className="file-route-code seal-type">{c.code}</span>
                  <span className="file-route-dur figures">{c.duration}</span>
                  {c.fee && <span className="file-route-fee figures">{c.fee}</span>}
                </a>
              </li>
            ))}
          </ul>

          <div className="page-actions">
            <a href="#courses" className="btn btn--primary">
              See both routes
              <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <Dots current={college.slug} light />
          </div>
        </Reveal>
        <Reveal className="file-hero-media">
          <Figure ratio="4 / 5" src={SHOTS.lawHero} alt="Scales of justice outside the college of law" />
        </Reveal>
      </div>
      <div className="ribbon" aria-hidden="true" />
    </section>

    <section className="section file-sheets" id="courses">
      <div className="container">
        <Reveal className="section-head section-head--center">
          <p className="eyebrow">Courses offered</p>
          <h2 className="h-lg">Two routes to the bar</h2>
        </Reveal>

        <div className="sheets">
          {college.courses.map((course) => (
            <Reveal className="sheet" as="article" key={course.id}>
              <Figure
                className="sheet-media"
                ratio="16 / 9"
                src={COURSE_SHOT[course.id]}
                alt={course.name}
              />
              <div className="sheet-body">
                <h3 className="sheet-title seal-type">{course.code}</h3>
                <p className="sheet-dur figures">{course.duration}</p>
                <p className="sheet-summary">{course.summary}</p>
                {course.fee && (
                  <p className="sheet-fee figures">
                    <span>Annual fee</span>
                    <strong>{course.fee}</strong>
                  </p>
                )}
                <Chips items={course.highlights} max={12} />
                <Entry course={course} />
                <a href="/admissions" className="btn btn--primary">
                  Apply for {course.code}
                  <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* the practical training, as pictures first */}
    {college.practice && (
      <section className="section file-practice">
        <div className="container">
          <Reveal className="section-head">
            <p className="eyebrow">Beyond the classroom</p>
            <h2 className="h-lg">Practical legal learning</h2>
          </Reveal>
          <div className="practice-row">
            {college.practice.map((p) => (
              <Reveal className="practice-card" as="article" key={p.title}>
                <Figure ratio="4 / 3" src={SHOTS.practice[p.title]} alt={p.title} label={p.title} size="900×675" />
                <h3 className="practice-title">{p.title}</h3>
                {p.items.length > 0 && <Chips items={p.items} max={4} />}
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    )}

    {college.awards && (
      <section className="section file-awards">
        <div className="container">
          <Reveal className="section-head section-head--center">
            <p className="eyebrow">Support with fees</p>
            <h2 className="h-md">Scholarships</h2>
          </Reveal>
          <Reveal className="award-row">
            {college.awards.map((a) => (
              <div className="award" key={a.title}>
                <h3>{a.title}</h3>
                <p>{a.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    )}
  </>
);

/* ------------------------------------------------------------------ router */
const LAYOUTS = {
  medical: Medical,
  "allied-healthcare": Allied,
  management: Management,
  law: Law,
};

const College = () => {
  const { slug } = useParams();
  const college = findCollege(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!college) return <Navigate to="/home" replace />;

  const Layout = LAYOUTS[college.slug] || Medical;

  return (
    <div className={`college college--${college.slug}`}>
      <Layout college={college} />
      <CareersStrip college={college} />
      <Siblings current={college.slug} />
      <CtaBand college={college} />
    </div>
  );
};

export default College;
